import Pusher from "pusher-js";

import { PUSHER_CLUSTER, PUSHER_KEY } from "../../constant/environment";
import { API_METHODS } from "../../enums";
import { fetchApi } from "../../utils/apiFunction";

// Neutral, app-wide Pusher connection: ONE socket, shared by independent feature
// modules (intent/ and searchProgress/), each of which subscribes its OWN
// channel via getPusher(). Pusher bills per connection, not per channel — so a
// single socket with N channels is the right shape, and the features stay fully
// decoupled (neither owns the other).
//
// Auth is connection-level here: the customHandler signs ANY channel the user
// subscribes (it just forwards channel_name to the pusher-auth endpoint), so
// feature modules add no auth code. Lifecycle (init/disconnect) is owned by
// <PusherBootstrap/>; features only subscribe/unsubscribe their channel.
let pusher: Pusher | null = null;
let currentKey: string | null = null; // `${sessionId}|${userId}|${jwt}` identity guard

const buildPusher = (sessionId: string, userId: string, jwt: string): Pusher =>
  new Pusher(PUSHER_KEY, {
    cluster: PUSHER_CLUSTER,
    forceTLS: true,
    channelAuthorization: {
      transport: "ajax",
      endpoint: "",
      customHandler: (params, callback) => {
        const { socketId, channelName } = params;
        console.log(`[pusher] auth request channel=${channelName} socket=${socketId}`);
        fetchApi(jwt, "intent/pusher/auth", API_METHODS.POST, {
          session_id: sessionId,
          userId,
          socket_id: socketId,
          channel_name: channelName,
        })
          .then((response) => {
            const authData = response?.data ?? response;
            if (authData?.auth) {
              callback(null, authData);
            } else {
              callback(new Error("pusher auth: missing auth field"), null);
            }
          })
          .catch((err) => callback(err as Error, null));
      },
    },
  });

export function initPusher(sessionId: string, userId: string, jwt: string): Pusher | null {
  if (!PUSHER_KEY || !sessionId || !userId || !jwt) return null;
  const key = `${sessionId}|${userId}|${jwt}`;
  if (pusher && currentKey === key) return pusher;
  if (pusher) disconnectPusher();
  console.log(`[pusher] init session=${sessionId} userId=${userId} key=${PUSHER_KEY.slice(0, 6)}…`);
  currentKey = key;
  pusher = buildPusher(sessionId, userId, jwt);
  pusher.connection.bind("state_change", (s: { previous: string; current: string }) => {
    console.log(`[pusher] socket state ${s.previous} → ${s.current}`);
  });
  return pusher;
}

export function getPusher(): Pusher | null {
  return pusher;
}

export function disconnectPusher(): void {
  if (!pusher) return;
  pusher.disconnect();
  pusher = null;
  currentKey = null;
}
